// Ground map rules shared by the browser and the Worker: scoring and the wave plan.

export type EnemyKind = "infantry" | "vehicle" | "armor";

export interface GroundStats {
  infantryKilled: number;
  vehiclesKilled: number;
  armorKilled: number;
  /** Waves fully resolved (every enemy destroyed or through) while the base still stood. */
  wavesCleared: number;
  shellsFired: number;
}

export const GROUND_POINTS = {
  infantry: 50,
  vehicle: 100,
  armor: 250,
  wave: 300,
  shell: -2,
} as const;

export function scoreGround(s: GroundStats): number {
  return (
    s.infantryKilled * GROUND_POINTS.infantry +
    s.vehiclesKilled * GROUND_POINTS.vehicle +
    s.armorKilled * GROUND_POINTS.armor +
    s.wavesCleared * GROUND_POINTS.wave +
    s.shellsFired * GROUND_POINTS.shell
  );
}

/** Enemies in wave `n` (1-based). */
export function waveSize(n: number): Record<EnemyKind, number> {
  return {
    infantry: 3 + n,
    vehicle: Math.max(0, n - 1),
    armor: Math.max(0, Math.floor((n - 2) / 2)),
  };
}

/** Total enemies of each kind across waves 1..n. */
export function spawnedThrough(n: number): Record<EnemyKind, number> {
  const total = { infantry: 0, vehicle: 0, armor: 0 };
  for (let w = 1; w <= n; w++) {
    const s = waveSize(w);
    total.infantry += s.infantry;
    total.vehicle += s.vehicle;
    total.armor += s.armor;
  }
  return total;
}

export const BASE_HP = 20;

/** Base HP lost when an enemy of each kind reaches the base. */
export const BASE_DAMAGE: Record<EnemyKind, number> = { infantry: 1, vehicle: 2, armor: 4 };

/**
 * The least base damage a game with these stats can have taken. Every enemy in a cleared wave was
 * either killed or reached the base; kills from the final, uncleared wave only make this more lenient.
 */
export function minimumLeakDamage(s: GroundStats): number {
  const spawned = spawnedThrough(s.wavesCleared);
  const leaked = (kind: EnemyKind, killed: number) => Math.max(0, spawned[kind] - killed) * BASE_DAMAGE[kind];
  return leaked("infantry", s.infantryKilled) + leaked("vehicle", s.vehiclesKilled) + leaked("armor", s.armorKilled);
}

/** No wave can resolve faster than this: enemies have to cross the map first. */
export const MIN_WAVE_SECONDS = 8;
